import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes, css } from 'styled-components';
import { Link } from 'react-router-dom';
import { User, Terminal, Check, ArrowRight } from 'lucide-react';
import { ThinkingOrb } from 'thinking-orbs';
import { breakpoints } from '@/layouts/breakpoints';
import TextType from '@/components/landing/common/TextType';
import GlyphShader from '@/components/landing/common/GlyphShaderLight';


/* ---------------------------------------------------------------- */
/* content                                                           */
/* ---------------------------------------------------------------- */


const TINT_OPTIONS: [number, number, number][] = [
  [1, 1, 1],
  // [0, 1, 1],
  // [1, 0, 1],
  // [1, 1, 0],
];

function randomGlyphConfig() {
  return {
    frequency: Math.floor(Math.random() * 10) + 1,
    noiseStrength: Math.random() * 0.9 + 0.1,
    cell: Math.floor(Math.random() * 10) + 5,
    distortAmp: Math.random() + 1,
    tint: TINT_OPTIONS[Math.floor(Math.random() * TINT_OPTIONS.length)],
    seed1: Math.random() * 10 + 1,
    seed2: Math.random() * 10 + 1,
  };
}

const conversations = [
  {
    dataset: 'alt_signals',
    query: 'What\u2019s unusual about semiconductor supply chains this week?',
    tool: 'get_alt_signals({ sector: "semiconductors", window: "7d" })',
    response:
      'Advanced-packaging equipment imports into Taiwan are up 23% week-over-week, and satellite imagery shows higher utilization at three major fabs.',
  },
  {
    dataset: 'sentiment',
    query: 'Any early sentiment signals ahead of the Fed decision?',
    tool: 'get_sentiment({ topic: "fomc_decision", sources: ["news","options_flow"] })',
    response:
      'Options flow is tilting toward longer-dated puts on rate-sensitive sectors, and social sentiment has cooled sharply since Tuesday.',
  },
  {
    dataset: 'anomalies',
    query: 'Flag any anomalies in retail foot traffic data this month.',
    tool: 'detect_anomalies({ dataset: "foot_traffic", window: "30d" })',
    response:
      'Three regional retailers show foot traffic diverging more than 2 standard deviations from seasonal baseline \u2014 two trending up ahead of earnings.',
  },
  {
    dataset: 'options_flow',
    query: 'Is there options activity flagging risk in regional banks?',
    tool: 'get_options_flow({ sector: "regional_banks", window: "5d" })',
    response:
      'Put/call skew on the regional bank basket has widened to its highest level since March, concentrated in three names.',
  },
  {
    dataset: 'shipping',
    query: 'Any shipping data pointing to a slowdown in consumer demand?',
    tool: 'get_shipping_signals({ route: "trans_pacific", window: "14d" })',
    response:
      'Trans-Pacific container bookings are down 11% versus the seasonal average, steepest in furniture and home goods.',
  },
  {
    dataset: 'social',
    query: 'How is retail sentiment trending on EV makers?',
    tool: 'get_social_sentiment({ theme: "ev_makers", window: "7d" })',
    response:
      'Mention volume is flat, but sentiment polarity turned modestly negative \u2014 mostly delivery-timeline complaints, not product criticism.',
  },
  {
    dataset: 'web_traffic',
    query: 'Is app engagement holding up for food-delivery names?',
    tool: 'get_web_traffic({ category: "food_delivery", window: "30d" })',
    response:
      'Daily active sessions are up 4% month-over-month across the category, though one competitor is losing share to the other two.',
  },
  {
    dataset: 'satellite',
    query: 'Any early read on retailer inventory levels this quarter?',
    tool: 'get_satellite_metrics({ sector: "big_box_retail", window: "45d" })',
    response:
      'Parking-lot traffic and warehouse throughput point to leaner inventory than last quarter, ahead of earnings.',
  },
  {
    dataset: 'card_spend',
    query: 'What does card-spend data show for airline bookings?',
    tool: 'get_card_spend({ category: "airlines", window: "30d" })',
    response:
      'Spend on domestic airfare is up 6% year-over-year, while international bookings cooled against a strong prior-year comp.',
  },
];

type Conversation = (typeof conversations)[number];

const DEFAULT_FIT_COUNT = 3;
const CALL_DELAY = 650;
const RESPONSE_DELAY = 2700;
const HOLD_TIME = 5400;
const EXIT_DURATION = 550;

// Placeholder set - replace with the actual states exposed by `thinking-orbs`.
const ORB_STATES = ['working', 'searching', 'solving', 'composing', 'shaping'] as const;

function shuffled<T>(arr: T[]): T[] {
  return [...arr].sort(() => Math.random() - 0.5);
}

/* ---------------------------------------------------------------- */
/* visibility hook \u2014 pause timers when scrolled off-screen          */
/* ---------------------------------------------------------------- */

function useInView(ref: React.RefObject<Element>) {
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { rootMargin: "100px", threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return inView;
}

/* ---------------------------------------------------------------- */
/* styles                                                             */
/* ---------------------------------------------------------------- */

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const dip = keyframes`
  0% { transform: translateY(0); }
  35% { transform: translateY(3px); }
  100% { transform: translateY(0); }
`;

const Section = styled.section`
  width: 100%;

  @media (max-width: 768px) {
    padding-bottom: 100px;
  }
 `;

const Container = styled.div`
  // max-width: 1920px;
  // margin: 0 auto;
`;

const Header = styled.div`
  max-width: 640px;
  margin-bottom: 48px;
  position: relative;
  background: rgba(var(--whiter), 0.5);

  @media (max-width: 900px) {
    background: rgba(var(--whiter), 0.5);
  }
  padding: 100px 40px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 72px 40px;
  }
  @media (max-width: ${breakpoints.small}) {
    padding: 56px 20px;
  }


  .graphic{
    width: 100vw;
    height: 100%;
    overflow: hidden;
    position: absolute;
    z-index: -1;
    top: 0px;
    left: 0px;
    opacity: 0.2
  }
`;

const Headline = styled.h2`
  font-size: 52px;
  line-height: 1.08;
  letter-spacing: -0.01em;
  margin: 0 0 20px 0;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 40px;
  }
  @media (max-width: ${breakpoints.small}) {
    font-size: 30px;
  }
`;

const Subhead = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: var(--grey);
  max-width: 480px;
  margin: 0 0 32px 0;
`;

const CtaRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`;

const CTA = styled(Link)`
    font-size: 16px;
    border: none;
    background-color: var(--black);
    color: var(--white);
    padding: 12px 20px;
    border-radius: 8px;
    cursor: pointer;
    display: flex;
    align-items: center;
    transition-duration: 0.3s;
    &:hover{
      background-color: #333;
    }

    &.secondary{
      background-color: transparent;
      color: var(--black);
      svg{
        padding-left: 10px;
        transition-duration: 0.3s;
      }
      &:hover{
        svg{
          transform: translateX(2px);
        }
        background-color: #eee;
      }

    }
 `;

/* ---- full-bleed wrapper \u2014 breaks the track out of Container's ---- */
/* ---- max-width/padding so it spans edge-to-edge of the viewport ---- */

const Bleed = styled.div`
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  padding-left:40px;

  @media (max-width: ${breakpoints.small}) {
    padding-left:20px;
  }
`;

/* ---- the slider track ---- */

const Track = styled.div`
  position: relative;
  width: 100%;
  height: var(--card-h, 320px);
  overflow: hidden;
  --card-w: 360px;
  --gap: 20px;
  animation: ${dip} 420ms ease-out;

  @media (max-width: ${breakpoints.medium}) {
    --card-w: 320px;
  }
  @media (max-width: ${breakpoints.small}) {
    --card-w: min(300px, 82vw);
    height: var(--card-h, 340px);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CardWindow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: var(--card-w);
  height: calc(100% - 2px);
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background: #ffffff;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transform: translateX(calc(var(--slot) * (var(--card-w) + var(--gap))));
  transition: transform 550ms cubic-bezier(0.22, 1, 0.36, 1), opacity 550ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: opacity 250ms ease;
  }
`;

const TerminalBar = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 14px;
  border-bottom: 1px solid #e2e2e2;
  background: #f2f2f2;
  flex-shrink: 0;
`;

const PromptPath = styled.span`
  font-family: ui-monospace, "SF Mono", "Roboto Mono", monospace;
  font-size: 11px;
  color: #8a8a8a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardBody = styled.div`
  padding: 18px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
`;

const Line = styled.div`
  animation: ${fadeUp} 0.4s ease both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const MessageRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
`;

const Avatar = styled.div<{ $variant: 'user' | 'bot' }>`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$variant === 'user' ? '#ececec' : '#161616')};
  color: ${(p) => (p.$variant === 'user' ? '#4d4d4d' : '#ffffff')};
  img{
    width: 80%;
    height: auto;
  }
`;

const MessageContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const SenderLabel = styled.span`
  font-family: ui-monospace, "SF Mono", "Roboto Mono", monospace;
  font-size: 10px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #a3a3a3;
  display: block;
  margin-bottom: 5px;
`;

const QueryText = styled.span`
  font-size: 14.5px;
  line-height: 1.45;
  color: #171717;
`;

const ToolLine = styled.div`
  font-family: ui-monospace, "SF Mono", "Roboto Mono", monospace;
  font-size: 11px;
  color: #3d3d3d;
  background: #f2f2f2;
  border: 1px solid #dedede;
  border-radius: 5px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;

  span:first-child {
    word-break: break-word;
  }
`;

const ToolStatus = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: #171717;
`;

const Response = styled.div`
  font-size: 14px;
  line-height: 1.55;
  color: #262626;
  margin: 0;
`;

/* ---------------------------------------------------------------- */
/* card                                                                */
/* ---------------------------------------------------------------- */

type Phase = 'query' | 'calling' | 'response';

interface CardData {
  id: string;
  convo: Conversation;
  slot: number;
}

// A card only "wakes up" and runs its calling -> response animation once it
// occupies `activeSlot` (the last FULLY visible slot). Cards still queued to
// its right (including the clipped, full-bleed overflow slot) sit idle
// showing just the analyst query. Cards that have already passed through
// `activeSlot` snap straight to the resolved response state and stay there
// as they continue drifting left toward eviction.
const AxionCard = ({
  convo,
  slot,
  activeSlot,
  inView,
}: {
  convo: Conversation;
  slot: number;
  activeSlot: number;
  inView: boolean;
}) => {
  const [phase, setPhase] = useState<Phase>(() => (slot < activeSlot ? 'response' : 'query'));
  const hasStartedRef = useRef(slot < activeSlot);
  const orbState = useMemo(
    () => ORB_STATES[Math.floor(Math.random() * ORB_STATES.length)],
    []
  );

  useEffect(() => {
    if (!inView) return;

    if (slot > activeSlot) {
      // still queued (or sitting in the clipped overflow slot) - do nothing yet
      return;
    }

    if (slot < activeSlot) {
      // already had its turn - make sure it's resolved, no animation
      setPhase((p) => (p === 'response' ? p : 'response'));
      return;
    }

    // slot === activeSlot: this card is now the active, animating one
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const t1 = window.setTimeout(() => setPhase('calling'), CALL_DELAY);
    const t2 = window.setTimeout(() => setPhase('response'), RESPONSE_DELAY);
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [slot, activeSlot, inView]);

  const offscreen = slot < 0;

  return (
    <CardWindow
      style={{ ['--slot' as string]: slot, opacity: offscreen ? 0 : 1 } as React.CSSProperties}
    >
      <TerminalBar>
        <Terminal size={12} color="#8a8a8a" />
        <PromptPath>analyst@axion:~/data/{convo.dataset}$</PromptPath>
      </TerminalBar>
      <CardBody aria-live="polite">
        <MessageRow>
          <Avatar $variant="user">
            <User size={13} />
          </Avatar>
          <MessageContent>
            <SenderLabel>Analyst</SenderLabel>
            <QueryText>{convo.query}</QueryText>
          </MessageContent>
        </MessageRow>

        {phase !== 'query' && (
          <Line>
            <MessageRow>
              <Avatar $variant="bot">
                <img src="/logo-light.png" alt="AxionQuant Avatar" fetchPriority="high" />
              </Avatar>
              <MessageContent>
                <SenderLabel>Axion</SenderLabel>
                <ToolLine>
                  <span>&rarr; {convo.tool}</span>
                  <ToolStatus>
                    {phase === 'response' ? (
                      <Check size={12} />
                    ) : (
                      <ThinkingOrb state={orbState} size={20} />
                    )}
                  </ToolStatus>
                </ToolLine>
                {phase === 'response' && <Response>
                  <TextType
                    text={convo.response}
                    typingSpeed={10}
                    loop={false}
                    cursorCharacter={'_'}
                  />
                </Response>}
              </MessageContent>
            </MessageRow>
          </Line>
        )}
      </CardBody>
    </CardWindow>
  );
};

/* ---------------------------------------------------------------- */
/* section                                                             */
/* ---------------------------------------------------------------- */

const MCPSection = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const inView = useInView(trackRef);

  const pool = useMemo(() => shuffled(conversations), []);
  const poolIndex = useRef(0);
  const nextId = useRef(0);

  const [cards, setCards] = useState<CardData[]>([]);

  // fitCount: how many cards are FULLY visible (floor) - activeSlot is
  // derived from this, so the animating card is always the rightmost fully
  // visible one, never the clipped edge card.
  // renderCount: total slots to render (ceil) so the trailing card still
  // clips off the edge for the full-bleed look.
  const [counts, setCounts] = useState({
    fitCount: DEFAULT_FIT_COUNT,
    renderCount: DEFAULT_FIT_COUNT,
  });
  const countsRef = useRef(counts);
  useEffect(() => {
    countsRef.current = counts;
  }, [counts]);

  const activeSlot = counts.fitCount - 1;

  // Keeps `cards` in sync with however many slots currently render,
  // preserving existing cards/order and only adding or trimming from the edges.
  const syncCardsToCount = (renderCount: number) => {
    setCards((prev) => {
      const sorted = [...prev].filter((c) => c.slot >= 0).sort((a, b) => a.slot - b.slot);

      if (sorted.length === renderCount) {
        return sorted.map((c, i) => ({ ...c, slot: i }));
      }

      if (sorted.length < renderCount) {
        const additions: CardData[] = [];
        while (sorted.length + additions.length < renderCount) {
          const convo = pool[poolIndex.current % pool.length];
          poolIndex.current += 1;
          additions.push({ id: `card-${nextId.current++}`, convo, slot: 0 });
        }
        return [...sorted, ...additions].map((c, i) => ({ ...c, slot: i }));
      }

      const trimmed = sorted.slice(sorted.length - renderCount);
      return trimmed.map((c, i) => ({ ...c, slot: i }));
    });
  };

  // Measure how many cards fit (fully + clipped) and keep it in sync on resize.
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const computeAndSync = () => {
      const styles = getComputedStyle(el);
      const cardW = parseFloat(styles.getPropertyValue('--card-w')) || 360;
      const gap = parseFloat(styles.getPropertyValue('--gap')) || 20;
      const width = el.clientWidth;
      const raw = (width + gap) / (cardW + gap);
      const fitCount = Math.max(1, Math.floor(raw));
      const renderCount = Math.max(fitCount, Math.ceil(raw));
      setCounts({ fitCount, renderCount });
      syncCardsToCount(renderCount);
    };

    computeAndSync();

    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(computeAndSync);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerBounce = () => {
    const el = trackRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    el.style.animation = 'none';
    void el.offsetHeight; // force reflow so the animation restarts
    el.style.animation = '';
  };

  // Single source of truth for advancing the conveyor: always evicts
  // whichever card currently sits at slot 0, shifts everyone else left by
  // exactly one, and brings a new card in at the back (the clipped edge
  // slot). New cards start idle and only start animating once they drift
  // into `activeSlot` on a later tick.
  const advance = () => {
    triggerBounce();

    const { renderCount } = countsRef.current;
    const newId = `card-${nextId.current++}`;
    const convo = pool[poolIndex.current % pool.length];
    poolIndex.current += 1;

    setCards((prev) => {
      const shifted = prev.map((c) => ({
        ...c,
        slot: c.slot === 0 ? -1 : c.slot - 1,
      }));
      return [...shifted, { id: newId, convo, slot: renderCount }];
    });

    // let the new card paint off-screen first, then animate it into the
    // clipped edge slot
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setCards((prev) =>
          prev.map((c) => (c.id === newId ? { ...c, slot: countsRef.current.renderCount - 1 } : c))
        );
      });
    });

    window.setTimeout(() => {
      setCards((prev) => prev.filter((c) => c.slot !== -1));
    }, EXIT_DURATION);
  };

  useEffect(() => {
    if (!inView) return;
    const interval = window.setInterval(advance, HOLD_TIME);
    return () => window.clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const config = useMemo(randomGlyphConfig, []);

  return (
    <Section>
      <Container>
        <Header>
          <Headline className="serif">Built for humans and LLMs alike</Headline>
          <div className="graphic">
            <GlyphShader  {...config} />
          </div>

          <Subhead>
            The same alternative data, shipped two ways: documented financial data APIs for
            your engineers, and native MCP servers so any agent can query it
            directly.
          </Subhead>
          <CtaRow>
            <CTA to="/dashboard/mcp">Get started </CTA>
            <CTA className="secondary" to="/developers/libraries">Explore client libraries <ArrowRight width={12}/> </CTA>
          </CtaRow>
        </Header>
      </Container>

      <Bleed>
        <Track ref={trackRef}>
          {cards.map((c) => (
            <AxionCard
              key={c.id}
              convo={c.convo}
              slot={c.slot}
              activeSlot={activeSlot}
              inView={inView}
            />
          ))}
        </Track>
      </Bleed>
    </Section>
  );
};

export default MCPSection;
